import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Link,
} from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./global.css";
import { ThemeProviderWrapper } from "./context/ThemeContext";
import { AuthProvider } from "./context/AuthContext";
import companyLogo from "./images/ACMlogobgremove.png";

// Import all your components
import AdminLogin from "./components/AdminLogin";
import AdminForgotPassword from "./components/AdminForgotPassword";
import UserForgotPassword from "./components/UserForgotPassword";
import UserLogin from "./components/UserLogin";
import AdminDashboard from "./components/AdminDashboard";
import DirectorDashboardWrapper from "./components/DirectorDashboardWrapper";
import ZonalHeadDashboardWrapper from "./components/ZonalHeadDashboardWrapper";

// --- START: FIX ---
// 1. Remove the incorrect Wrapper import and import the actual dashboard component.
import TeamLeadDashboard from "./components/TeamLeadDashboard";
import BranchManagerDashboard from "./components/BranchManagerDashboard"; // <-- ADD THIS IMPORT
// --- END: FIX ---

import RelationshipManagerDashboard from "./components/RelationshipManagerDashboard";
import ManageUsers from "./components/ManageUsers";
import DeletedUsers from "./components/DeletedUsers";
import RegisterDirector from "./components/RegisterDirector";
import RegisterZonalHead from "./components/RegisterZonalHead";
import RegisterBranchManager from "./components/RegisterBranchManager";
import RegisterTeamLead from "./components/RegisterTeamLead";
import RegisterRelationshipManager from "./components/RegisterRelationshipManager";
import RegisterAdmin from "./components/RegisterAdmin";
import DirectorsList from "./components/DirectorsList";
import ZonalHeadsList from "./components/ZonalHeadsList";
import BranchManagersList from "./components/BranchManagersList";
import TeamLeadsList from "./components/TeamLeadsList";
import RelationshipManagersList from "./components/RelationshipManagersList";
import AdminsList from "./components/AdminsList";
import PerformanceStatistics from "./components/PerformanceStatistics";
import MonthlyPerformanceReports from "./components/MonthlyPerformanceReports";
import AdminReport from "./components/AdminReport";
import AdminMonthlyReport from "./components/AdminMonthlyReport";
import UserMonthlyReport from "./components/UserMonthlyReport";
import ProfilePage from "./components/ProfilePage";
import ChangePassword from "./components/ChangePassword";
import AdminProfile from "./components/AdminProfile";
import TeamManagement from "./components/TeamManagement";

// A reusable Protected Route component for security
const ProtectedRoute = ({
  children,
  tokenKey = "userToken",
  redirectTo = "/user-login",
}) => {
  const isAuthenticated = localStorage.getItem(tokenKey);
  return isAuthenticated ? children : <Navigate to={redirectTo} />;
};

function App() {
  return (
    <ThemeProviderWrapper>
      <Router>
        <AuthProvider>
          <div className="App">
            <Routes>
              {/* Landing Page */}
              <Route
                path="/"
                element={
                  <div className="home-page-container">
                    <div className="home-left-panel"></div>
                    <div className="home-right-panel">
                      <div className="home-login-box">
                        <img
                          src={companyLogo}
                          alt="Company Logo"
                          className="home-company-logo"
                        />
                        <h1 className="home-title">Sales Tracking System</h1>
                        <p className="home-subtitle">
                          Monitor your sales performance efficiently.
                        </p>
                        <div className="home-button-group">
                          <Link
                            to="/admin-login"
                            className="home-login-button home-admin-button"
                          >
                            Admin Login
                          </Link>
                          <Link
                            to="/user-login"
                            className="home-login-button home-user-button"
                          >
                            User Login
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                }
              />

              {/* Public Authentication Routes */}
              <Route path="/admin-login" element={<AdminLogin />} />
              <Route
                path="/admin-forgot-password"
                element={<AdminForgotPassword />}
              />
              <Route path="/user-login" element={<UserLogin />} />
              <Route
                path="/user-forgot-password"
                element={<UserForgotPassword />}
              />

              {/* Protected General Routes */}
              <Route
                path="/change-password"
                element={
                  <ProtectedRoute>
                    <ChangePassword />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />

              {/* ========================================================= */}
              {/* ========= USER DASHBOARD ROUTES ========= */}
              {/* ========================================================= */}

              {/* Relationship Manager Dashboard Route */}
              <Route
                path="/relationship-manager-dashboard/:section?/:date?"
                element={
                  <ProtectedRoute>
                    <RelationshipManagerDashboard />
                  </ProtectedRoute>
                }
              />

              {/* Team Lead Dashboard Routes */}
              <Route
                path="/team-lead-dashboard"
                element={
                  <ProtectedRoute>
                    <Navigate to="/team-lead-dashboard/dashboard" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/team-lead-dashboard/:section/*"
                element={
                  <ProtectedRoute>
                    <TeamLeadDashboard />
                  </ProtectedRoute>
                }
              />

              {/* --- START: FIX --- */}
              {/* 2. Update the Branch Manager routes to match the Team Lead structure. */}
              {/* Base route that redirects to the default dashboard view */}
              <Route
                path="/branch-manager-dashboard"
                element={
                  <ProtectedRoute>
                    <Navigate
                      to="/branch-manager-dashboard/dashboard"
                      replace
                    />
                  </ProtectedRoute>
                }
              />
              {/* Wildcard route that handles all other sub-paths */}
              <Route
                path="/branch-manager-dashboard/:section/*"
                element={
                  <ProtectedRoute>
                    <BranchManagerDashboard />
                  </ProtectedRoute>
                }
              />
              {/* --- END: FIX --- */}

              {/* Director Dashboard Routes */}
              <Route
                path="/director-dashboard"
                element={
                  <ProtectedRoute>
                    <Navigate to="/director-dashboard/dashboard" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/director-dashboard/:section/*"
                element={
                  <ProtectedRoute>
                    <DirectorDashboardWrapper />
                  </ProtectedRoute>
                }
              />

              {/* Zonal Head Dashboard Routes */}
              <Route
                path="/zonal-head-dashboard"
                element={
                  <ProtectedRoute>
                    <Navigate to="/zonal-head-dashboard/dashboard" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/zonal-head-dashboard/:section/*"
                element={
                  <ProtectedRoute>
                    <ZonalHeadDashboardWrapper />
                  </ProtectedRoute>
                }
              />

              {/* ========================================================= */}
              {/* ========= ADMIN ROUTES ========= */}
              {/* ========================================================= */}

              <Route
                path="/admin-dashboard"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin-profile"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <AdminProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manage-users"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <ManageUsers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/deleted-users"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <DeletedUsers />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/team-management"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <TeamManagement />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/register-director"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <RegisterDirector />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/register-zonal-head/:directorId?"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <RegisterZonalHead />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/register-branch-manager/:zonalId?"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <RegisterBranchManager />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/register-team-lead/:branchId?"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <RegisterTeamLead />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/register-relationship-manager/:teamLeadId?"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <RegisterRelationshipManager />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/register-admin"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <RegisterAdmin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/directors-list"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <DirectorsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/zonal-heads-list"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <ZonalHeadsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/branch-managers-list"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <BranchManagersList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/team-leads-list"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <TeamLeadsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relationship-managers-list"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <RelationshipManagersList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admins-list"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <AdminsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/performance-statistics"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <PerformanceStatistics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/monthly-performance-reports"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <MonthlyPerformanceReports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin-report"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <AdminReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin-monthly-report/:userId"
                element={
                  <ProtectedRoute
                    tokenKey="adminToken"
                    redirectTo="/admin-login"
                  >
                    <AdminMonthlyReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/monthly-report"
                element={
                  <ProtectedRoute>
                    <UserMonthlyReport />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </div>
        </AuthProvider>
      </Router>
    </ThemeProviderWrapper>
  );
}

export default App;
